/**
 * Resolution helpers for the ``doppler://`` URI scheme used by Phase 3
 * citation rendering. The backend's marker post-processor emits opaque
 * URIs because the renderer doesn't know how to mint signed URLs; the
 * frontend resolves them lazily on click via the existing files /
 * workbook-blocks APIs.
 *
 * Supported forms:
 *   doppler://pdf/<file_id>[?page=<n>][&hl=<quote>]
 *   doppler://excel/<file_id>[?sheet=<name>]              ← source workbook
 *   doppler://excel/artifact/<block_id>[?sheet=<name>]   ← modified workbook
 *   doppler://doc/<file_id>[?message=<n>]                ← comms projection
 *   doppler://cite/<event_id>                            ← citation anchor
 */

import { chatApi } from "../services/chatApi";
import { getFileSignedUrl } from "../services/fileApi";
import { FileSubkind, FileType } from "../types/fileType";
import { isCitationTabEnabled } from "./featureFlags";

const DOPPLER_SCHEME = "doppler://";

/** Upper bound on the ``hl`` quote, mirroring the backend's cap (DOP-403).
 * Anything longer is truncated: a highlight is a hint, not an identifier. */
const HIGHLIGHT_MAX_CHARS = 200;

export const isDopplerUri = (href: string | undefined | null): boolean =>
	typeof href === "string" && href.startsWith(DOPPLER_SCHEME);

export interface ParsedDopplerUri {
	kind: FileType;
	/** ``FILE`` for source workbooks / PDFs; ``ARTIFACT`` for the
	 * modified-workbook block id (post-edit, chat-scoped). */
	subkind: FileSubkind;
	target: string;
	page?: number;
	sheet?: string;
	/** Verbatim quote to highlight on ``page`` in the in-app PDF viewer.
	 * Carried by the backend as ``&hl=<urlencoded quote>`` (DOP-403) and capped
	 * server-side at 200 characters; the viewer treats a miss as "no highlight",
	 * never as an error. */
	highlight?: string;
	/** A NUMBER to highlight on ``page``, for citations that have no quote — a
	 * KPI value read from a PDF cites a page and a column, not a sentence.
	 * Carried as ``&num=<value>`` with optional ``&unit=`` and ``&col=`` (the
	 * header of the column it was read from). A hint exactly as ``hl`` is: a
	 * figure the viewer cannot place leaves the page unmarked. */
	number?: { value: number; unit?: string; column?: string };
	/** Single cell to focus/highlight in the in-app viewer, e.g. ``C11``. */
	cell?: string;
	/** Range to focus/highlight, e.g. ``C23:N23``. */
	range?: string;
	/** Display filename for the viewer header (the URI otherwise only carries
	 * the file id). */
	filename?: string;
	/** 1-based message ordinal inside a comms projection document.
	 *
	 * KNOWN LIMIT (v1): parsed and carried, but not honoured. There is no
	 * in-app markdown viewer, so a `doc` URI resolves to a signed URL and the
	 * projection opens at the top rather than at its `## Message N` heading.
	 * Anchoring needs a viewer that owns the rendered document. */
	message?: number;
}

const isFileType = (value: string): value is FileType =>
	value === FileType.PDF ||
	value === FileType.EXCEL ||
	value === FileType.DOC ||
	value === FileType.CITE;

export const parseDopplerUri = (href: string): ParsedDopplerUri | null => {
	if (!isDopplerUri(href)) return null;
	let url: URL;
	try {
		// URL parser needs a real scheme; doppler:// is non-special so it
		// works in modern browsers (Chrome/Firefox/Safari all accept it).
		url = new URL(href);
	} catch {
		return null;
	}
	const hostname = url.hostname;
	if (!isFileType(hostname)) return null;
	const kind: FileType = hostname;
	// Path is ``/<target>`` for files and ``/artifact/<block_id>`` for
	// modified-workbook artifacts. Normalize to (subkind, target).
	const segments = url.pathname.replace(/^\//, "").split("/");
	let subkind: FileSubkind = FileSubkind.FILE;
	let target: string;
	if (segments[0] === FileSubkind.ARTIFACT && segments.length >= 2) {
		subkind = FileSubkind.ARTIFACT;
		target = segments.slice(1).join("/");
	} else {
		target = segments.join("/");
	}
	if (!target) return null;
	const pageRaw = url.searchParams.get("page");
	const page = pageRaw ? Number(pageRaw) : undefined;
	// ``hl`` is a display hint, not an identifier: an over-long or blank value
	// is trimmed away rather than rejected, so a bad quote never costs the reader
	// the page they asked for.
	const highlightRaw = url.searchParams.get("hl");
	const highlight = highlightRaw?.trim().slice(0, HIGHLIGHT_MAX_CHARS) || undefined;
	const numRaw = url.searchParams.get("num");
	const numValue = numRaw === null || numRaw.trim() === "" ? NaN : Number(numRaw);
	const number = Number.isFinite(numValue)
		? {
				value: numValue,
				unit: url.searchParams.get("unit") ?? undefined,
				column:
					url.searchParams.get("col")?.trim().slice(0, HIGHLIGHT_MAX_CHARS) ||
					undefined,
			}
		: undefined;
	const sheet = url.searchParams.get("sheet") ?? undefined;
	const cell = url.searchParams.get("cell") ?? undefined;
	const range = url.searchParams.get("range") ?? undefined;
		const filename = url.searchParams.get("file") ?? undefined;
	const messageRaw = url.searchParams.get("message");
	const message = messageRaw ? Number(messageRaw) : undefined;
	return {
		kind,
		subkind,
		target,
		page:
			Number.isFinite(page) && (page ?? 0) > 0 ? Math.floor(page as number) : undefined,
		highlight,
		number,
		sheet,
		cell,
		range,
		filename,
		message:
			Number.isFinite(message) && (message ?? 0) > 0 ? message : undefined,
	};
};

/**
 * Resolve a parsed ``doppler://`` URI to a short-lived signed URL for the
 * underlying file, without opening it. Used by the in-app spreadsheet viewer
 * to fetch the workbook bytes (the new-tab opener uses the same resolution).
 * Returns ``null`` when the URI can't be resolved (e.g. artifact without a
 * chatId, non-numeric file target).
 */
export const resolveDopplerSignedUrl = async (
	parsed: ParsedDopplerUri,
	opts: OpenDopplerUriOpts = {},
): Promise<string | null> => {
	if (parsed.kind === FileType.CITE) {
		// A citation anchor names an evidence event, not a file. It has nothing
		// to resolve, and asking the files API for "evt_abc" would 404 — the
		// anchor's target is the audit trail, reached through the chain.
		return null;
	}
	if (parsed.kind === FileType.EXCEL && parsed.subkind === FileSubkind.ARTIFACT) {
		if (!opts.chatId) {
			console.warn("[evidence] cannot resolve Excel artifact without chatId");
			return null;
		}
		try {
			const { signed_url } = await chatApi.getModifiedWorkbookSignedUrl(
				opts.chatId,
				parsed.target,
			);
			return signed_url;
		} catch (err) {
			console.error("[evidence] failed to resolve workbook-artifact URI:", err);
			return null;
		}
	}

	const fileId = Number(parsed.target);
	if (!Number.isInteger(fileId) || fileId <= 0) {
		console.warn("[evidence] non-numeric doppler target:", parsed.target);
		return null;
	}
	try {
		const { signed_url } = await getFileSignedUrl(
			fileId,
			300,
			parsed.kind === FileType.PDF ? parsed.page : undefined,
		);
		return signed_url;
	} catch (err) {
		console.error("[evidence] failed to resolve doppler URI:", err);
		return null;
	}
};

/**
 * The file page that shows this citation, or ``null`` when the citation has no
 * file page: a chat-scoped workbook artifact, a comms projection, an evidence
 * anchor. The citation rides along as the URI's own query string (``page``,
 * ``hl``, ``num``/``unit``/``col``, ``sheet``, ``cell``, ``range``, ``file``),
 * so the file page reads it with the same parser — see ``citationFromLocation``.
 */
export const citationTabPath = (href: string): string | null => {
	const parsed = parseDopplerUri(href);
	if (!parsed || parsed.subkind !== FileSubkind.FILE) return null;
	if (parsed.kind !== FileType.PDF && parsed.kind !== FileType.EXCEL) return null;
	const fileId = Number(parsed.target);
	if (!Number.isInteger(fileId) || fileId <= 0) return null;
	const query = href.indexOf("?");
	const search = query === -1 ? "" : href.slice(query);
	return `/files/file/${fileId}${search ? `${search}&cite=${parsed.kind}` : `?cite=${parsed.kind}`}`;
};

/**
 * The citation a file page was opened for, rebuilt from its URL by the same
 * parser the chat uses. ``null`` for a file page opened from the Deal Room.
 */
export const citationFromLocation = (
	fileId: number,
	search: string,
): ParsedDopplerUri | null => {
	const params = new URLSearchParams(search);
	const kind = params.get("cite");
	if (kind !== FileType.PDF && kind !== FileType.EXCEL) return null;
	params.delete("cite");
	const rest = params.toString();
	return parseDopplerUri(`doppler://${kind}/${fileId}${rest ? `?${rest}` : ""}`);
};

/**
 * Open a citation on its file page, in a tab named for the file: a second
 * citation into the same document moves that tab's highlight instead of
 * opening another one. Returns false when the citation has no file page or the
 * flag is off, so the caller falls through to the modal viewers.
 */
export const openCitationTab = (href: string): boolean => {
	if (!isCitationTabEnabled()) return false;
	const path = citationTabPath(href);
	if (!path) return false;
	const fileId = parseDopplerUri(href)?.target;
	window.open(`${window.location.origin}${path}`, `doppler-file-${fileId}`);
	return true;
};

interface OpenDopplerUriOpts {
	/** Required when the URI references a chat-scoped artifact. */
	chatId?: string | null;
}

/** Open a PDF or Excel file in a new tab via a short-lived signed URL.
 * Falls back to a console warning if the resolver fails — caller controls
 * the click event so we don't double-handle navigation. */
export const openDopplerUri = async (
	href: string,
	opts: OpenDopplerUriOpts = {},
): Promise<void> => {
	if (openCitationTab(href)) return;
	const parsed = parseDopplerUri(href);
	if (!parsed) return;
	const signedUrl = await resolveDopplerSignedUrl(parsed, opts);
	if (signedUrl) window.open(signedUrl, "_blank", "noopener");
};
